import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Decision, DecisionTree } from '../models/decisionTree';
import { useDecisionContext } from '../context/DecisionContext';

// 简化版的决策节点组件用于比较视图
const CompareDecisionNode: React.FC<{ decision: Decision, level?: number }> = ({ 
  decision, 
  level = 0 
}) => {
  const [expanded, setExpanded] = useState(false);
  
  // 计算概率颜色
  const getProbabilityColor = (probability: number): string => {
    if (probability >= 80) return 'bg-green-500';
    if (probability >= 60) return 'bg-green-400';
    if (probability >= 50) return 'bg-yellow-400';
    if (probability >= 30) return 'bg-orange-400';
    return 'bg-red-500';
  };
  
  return (
    <div className={`mb-4 ${level > 0 ? 'ml-4 pl-2 border-l-2 border-gray-200' : ''}`}>
      <div className="bg-white p-3 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
        <div className="flex items-start">
          <div 
            className={`flex-shrink-0 w-12 h-8 ${getProbabilityColor(decision.probability)} rounded-md flex items-center justify-center text-white font-bold mr-2`}
          >
            {decision.probability}%
          </div>
          <div className="flex-grow">
            <h4 className="text-md font-medium text-gray-800">{decision.title}</h4>
            <p className="text-gray-600 mt-1 text-sm line-clamp-2">{decision.description}</p>
            
            {decision.children.length > 0 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 flex items-center"
              >
                <svg
                  className={`h-3 w-3 mr-1 transform transition-transform ${expanded ? 'rotate-90' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                {expanded ? '收起' : '展开'} ({decision.children.length}个子选项)
              </button>
            )}
          </div>
        </div>
      </div>
      
      {expanded && decision.children.length > 0 && (
        <div className="mt-2 animate-fadeIn">
          {decision.children.map((child) => (
            <CompareDecisionNode
              key={child.id}
              decision={child}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// 决策树比较视图
const DecisionCompareView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { decisionTree } = useDecisionContext();
  const [selectedRecords, setSelectedRecords] = useState<DecisionTree[]>([]);
  const [availableRecords, setAvailableRecords] = useState<DecisionTree[]>([]);
  
  // 获取决策记录
  useEffect(() => {
    try {
      const recordsJson = localStorage.getItem('decisionRecords');
      if (recordsJson) {
        const records = JSON.parse(recordsJson);
        const trees = records.map((record: any) => record.decisionTree as DecisionTree);
        setAvailableRecords(trees);
      }
    } catch (error) {
      console.error('加载决策记录失败:', error);
    }
    
    // 检查是否从URL参数传递了比较ID
    const params = new URLSearchParams(location.search);
    const compareIds = params.get('ids')?.split(',');
    if (compareIds && compareIds.length > 0) {
      // 加载指定ID的决策树
      try {
        const recordsJson = localStorage.getItem('decisionRecords');
        if (recordsJson) {
          const records = JSON.parse(recordsJson);
          const selectedTrees: DecisionTree[] = [];
          
          compareIds.forEach(id => {
            const record = records.find((r: any) => r.id === id);
            if (record && record.decisionTree) {
              selectedTrees.push(record.decisionTree);
            }
          });
          
          if (selectedTrees.length > 0) {
            setSelectedRecords(selectedTrees);
          }
        }
      } catch (error) {
        console.error('加载指定决策记录失败:', error);
      }
    }
  }, [location.search]);
  
  // 添加当前决策树到比较
  const addCurrentTree = () => {
    if (decisionTree && !selectedRecords.find(r => r.question === decisionTree.question)) {
      setSelectedRecords(prev => [...prev, decisionTree]);
    }
  };
  
  // 添加已保存决策树到比较
  const addSavedTree = (tree: DecisionTree) => {
    if (!selectedRecords.find(r => r.question === tree.question)) {
      setSelectedRecords(prev => [...prev, tree]);
    }
  };
  
  // 从比较中移除决策树
  const removeTree = (index: number) => {
    setSelectedRecords(prev => prev.filter((_, i) => i !== index));
  };
  
  // 生成分享链接
  const generateShareLink = () => {
    // 假设我们可以通过问题来标识记录
    try {
      const recordsJson = localStorage.getItem('decisionRecords');
      if (recordsJson) {
        const records = JSON.parse(recordsJson);
        const ids: string[] = [];
        
        selectedRecords.forEach(tree => {
          const record = records.find((r: any) => 
            r.decisionTree && r.decisionTree.question === tree.question
          );
          if (record && record.id) {
            ids.push(record.id);
          }
        });
        
        if (ids.length > 0) {
          const baseUrl = window.location.origin;
          const url = `${baseUrl}/compare?ids=${ids.join(',')}`;
          
          // 复制到剪贴板
          navigator.clipboard.writeText(url)
            .then(() => alert('比较链接已复制到剪贴板！'))
            .catch(() => alert('无法复制链接，请手动复制：\n' + url));
        }
      }
    } catch (error) {
      console.error('生成分享链接失败:', error);
    }
  };
  
  return (
    <div className="w-full max-w-6xl mt-4">
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">决策树比较</h2>
          <div className="flex space-x-2">
            {decisionTree && (
              <button
                onClick={addCurrentTree}
                className="px-3 py-1 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 transition-colors flex items-center"
                disabled={selectedRecords.find(r => r.question === decisionTree.question) !== undefined}
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                添加当前决策树
              </button>
            )}
            {selectedRecords.length >= 2 && (
              <button
                onClick={generateShareLink}
                className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                生成分享链接
              </button>
            )}
          </div>
        </div>
        
        {/* 选择保存的决策树 */}
        {availableRecords.length > 0 && selectedRecords.length < 3 && (
          <div className="mb-6">
            <h3 className="text-md font-medium text-gray-700 mb-2">添加已保存的决策分析</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {availableRecords
                .filter(record => !selectedRecords.find(r => r?.question === record?.question))
                .slice(0, 6)
                .map((record, index) => (
                  <div 
                    key={index} 
                    className="p-3 border rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => addSavedTree(record)}
                  >
                    <p className="font-medium truncate">{record?.question || "未命名决策"}</p>
                    <p className="text-xs text-gray-500 mt-1">{record?.decisions?.length || 0}个决策选项</p>
                  </div>
                ))}
            </div>
          </div>
        )}
        
        {/* 比较视图 */}
        {selectedRecords.length > 0 ? (
          <div className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedRecords.map((tree, index) => (
                <div key={index} className="relative">
                  <button
                    onClick={() => removeTree(index)}
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 transform translate-x-1/2 -translate-y-1/2"
                    title="移除"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-2 truncate">{tree?.question || "未命名决策"}</h3>
                    <div className="overflow-y-auto max-h-[500px] pr-2">
                      {tree?.decisions?.map(decision => (
                        <CompareDecisionNode key={decision.id} decision={decision} />
                      )) || <p className="text-gray-500 italic">无决策数据</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">没有选择决策树</h3>
            <p className="mt-1 text-sm text-gray-500">请选择要比较的决策树</p>
          </div>
        )}
        
        {/* 指引文本 */}
        <div className="mt-8 bg-blue-50 p-4 rounded-md text-sm text-blue-800">
          <h4 className="font-medium">使用提示:</h4>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>最多可以同时比较3个决策树</li>
            <li>点击各选项下的"展开"可以查看子决策选项</li>
            <li>使用"生成分享链接"可以与他人分享您的比较结果</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DecisionCompareView; 